import type { ReactElement, ReactNode } from 'react';
import { useLandingHeroImagesQuery } from '@/entities/landing-image';
import { BrandLogo } from '@/shared/ui';

interface AuthBrandPanelProps {
  /** 크게 싣는 소개 문구 — 줄을 직접 나누려면 <br /> 를 넣는다. */
  readonly headline: ReactNode;
  readonly description: string;
}

/**
 * 인증 틀(로그인·회원가입·비밀번호 재설정) 왼쪽 브랜드 면 — HOME 첫 배너 사진을 흑연 그라데이션 아래 깔고 로고·라임 선·문구를 얹는다.
 * 배너가 없거나 불러오는 중이면 사진 없이 큰 SS 무늬와 라임·민트 빛 번짐으로 꾸민 흑연 판을 보인다.
 * 꾸밈이라 보조기기에서는 감춘다 — 화면 이름은 오른쪽 폼의 h1 이 준다.
 */
export const AuthBrandPanel = ({ headline, description }: AuthBrandPanelProps): ReactElement => {
  const photoUrl = useLandingHeroImagesQuery().data?.[0]?.imageUrl ?? null;

  return (
    <div
      aria-hidden
      className="relative isolate flex h-full flex-col overflow-hidden bg-primary text-white"
    >
      {/* 바탕 무늬 — 사진이 없을 때 보이고, 사진이 있으면 그 아래에 깔린다. */}
      <div className="absolute -top-24 -right-32 size-96 rounded-full bg-brand-lime/25 blur-3xl" />
      <div className="absolute -bottom-32 -left-24 size-96 rounded-full bg-brand-mint/20 blur-3xl" />
      <BrandLogo className="absolute top-1/4 -right-48 h-72 text-white/[0.05]" />

      {photoUrl !== null ? (
        <>
          <img src={photoUrl} alt="" className="absolute inset-0 size-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/25" />
        </>
      ) : null}

      <div className="relative flex flex-1 flex-col justify-between p-12 xl:p-16">
        <BrandLogo className="h-10 self-start" />
        <div>
          <span className="block h-1 w-16 bg-brand-lime" />
          <p className="mt-8 text-[clamp(2rem,3vw,2.75rem)] leading-tight font-bold tracking-[-0.02em]">
            {headline}
          </p>
          <p className="mt-5 text-[15px] text-white/75">{description}</p>
        </div>
      </div>
    </div>
  );
};
